import React, { useState, useEffect, useRef } from "react";
import moment from 'moment'
import useForm from '../../hooks/useForm';
import { FormField,  Modal, Button } from '@prosolve/common-components-ui'
import useDataSessions from '../../dataHooks/useDataSessions';
import ImagePlaceholder from '../../images/image-placeholder.jpg';
import { FaTrash } from 'react-icons/fa';

export default ({
  existingSessionData = {},
  onClose,
  onSuccess,
  onDeleteSession,
}) => {
  const isInitialRender = useRef(true);

  const {
    _id,
    date,
    startTime,
    endTime,
    name = '',
    moduleBlueprintId = ''
  } = existingSessionData;

  const isUpdate = Boolean(name)

  const initData = isUpdate 
    ?
    { date, 
      startTime, 
      endTime, 
      type: moduleBlueprintId ? 'Module' : 'Transition', 
      moduleBlueprintId, 
      transitionText: moduleBlueprintId ? '' : name }
    :
    { type: 'Module' }

  const {
    fetchModuleBlueprints
  } = useDataSessions()

  const formSchema = {
    requiredFields: ['date', 'startTime', 'endTime', 'type', 'moduleBlueprintId', 'transitionText'],
    initData
  };

  const formData = useForm(formSchema)

  useEffect(() => {
    let junkValueField;
    let resetValueField;
    let resetValue = '';
    // This is done to get around validation where dont need either moduleBlueprintId or transitionText depending on type value
    if (formData.values.type === 'Module') {
      resetValueField = 'moduleBlueprintId';
      junkValueField = 'transitionText';
      // Need to make sure on initial render we set value to existing if the data is there
      if (isInitialRender.current && moduleBlueprintId) {
        resetValue = moduleBlueprintId;
      }
    } else {
      resetValueField = 'transitionText';
      junkValueField = 'moduleBlueprintId'
      // Need to make sure on initial render we set value to existing if the data is there
      if (isInitialRender.current && name) {
        resetValue = name;
      }
    }

    if (isInitialRender.current) {
      isInitialRender.current = false;
    }

    formData.setValues({ ...formData.values, [resetValueField]: resetValue, [junkValueField]: '-' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values.type])

  const isFormValid = () => {
    const formDataValid = formData.isValid();
    if (formDataValid) {
      const startTimeMoment = moment(formData.values.startTime, 'hh:mm:ss');
      const endTimeMoment = moment(formData.values.endTime, 'hh:mm:ss');
      return startTimeMoment.isBefore(endTimeMoment)
    } else {
      return false;
    }
  }
  
  const [moduleBlueprints, setModuleBlueprints] = useState([])
  const [filterOptionsVal, setFilterOptionsVal] = useState(moduleBlueprintId || '')
  const [isLoading, setIsLoading] = useState(false);

  const onClickSubmit = async () => {
    if (isFormValid()) {
      if (formData.values.type === 'Module') {  
        const matchingBlueprint = moduleBlueprints.find(({ id }) => id === formData.values.moduleBlueprintId)
        if (matchingBlueprint) {
          const { name = '', image = '' } = matchingBlueprint;
          const newSessionData = {
            ...formData.values,
            name,
            image
          }
          delete newSessionData.type;
          delete newSessionData.transitionText;
          onSuccess(newSessionData, (isUpdate ? true : false), _id)
        }
      } else {
        const newSessionData = {
          ...formData.values,
          name: formData.values.transitionText,
          moduleBlueprintId: ''
        }
        delete newSessionData.transitionText;
        delete newSessionData.type;
        onSuccess(newSessionData, (isUpdate ? true : false), _id)
      }
    }
  }

  const getModuleBlueprints = async () => {
    setIsLoading(true)
    try {
      const mBlueprints = await fetchModuleBlueprints();
      setModuleBlueprints(mBlueprints)
    } catch (e) {
      setModuleBlueprints([])
    }
    setIsLoading(false)
  }

  useEffect(() => {
    getModuleBlueprints()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredModuleBlueprints = moduleBlueprints
  .reduce(function(filtered, moduleBlueprint) {
    const { id = '', name = '', image = '' } = moduleBlueprint;

    let passesFilter;
    if (filterOptionsVal) {
      if ( name.toLowerCase().includes(filterOptionsVal.toLowerCase()) || id.toLowerCase().includes(filterOptionsVal.toLowerCase())) {
        passesFilter = true;
      } else {
        passesFilter = false;
      }
    } else {
      passesFilter = true; // false;
    }

    const display = (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <img 
            style={{ 
              width: '100px', 
              // height: '50px', 
              borderRadius: '5px',
              objectFit: 'cover',
              objectPosition: 'top center'
            }} 
            src={image || ImagePlaceholder} 
            alt='eventImage' 
          />
        </div>
        <div style={{ 
          marginLeft: '20px'
        }}>
          {name}
        </div>
      </div>
    )

    if (passesFilter) {
      filtered.push({ value : id, text: name, display, sortText: name });
    }

    return filtered;
  }, [])
  .sort(({ sortText: textA }, {sortText: textB }) => {
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  })

  const typeOptions = [
    { text: 'Session', value: 'Module' },
    { text: 'Transition', value: 'Transition' },
  ]

  const footer = (
    <div style={{ 
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      columnGap: '10px',
      padding: '10px'
    }}>
      {
        isUpdate &&
        <Button
          style={{ color: '#ef0c0c'}}
          onClick={() => {
            onDeleteSession(existingSessionData)
          }}>
            <FaTrash style={{ color: '#ef0c0c', fontSize: '12px', marginRight: '5px' }} />
          <div>{'Delete Session'}</div>
        </Button>
      }
      <div style={{ flex: 1 }}>&nbsp;</div>
      <Button onClick={onClose}>
        {'Cancel'}
      </Button>
      <Button onClick={onClickSubmit} $colored={true}>
        {isUpdate ? 'Update Session' : 'Add Session'}
      </Button>
    </div>
  )

  return (
    <Modal title={isUpdate ? 'Update Session' : 'Add Session'} isAutoHeight={true} isOverflowVisible={true} onClose={onClose} footer={footer}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '15px'
      }}>
        <div style={{
          display: 'flex',
          columnGap: '15px'
        }}>
          <FormField
            style={{ flex: 1 }}
            fieldId='date'
            type='date'
            label='Date'
            placeholder=''
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='startTime'
            type='time'
            label='Start Time'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='endTime'
            type='time'
            label='End Time'
            formData={formData}
          />
        </div>

        <div style={{
          display: 'flex',
          columnGap: '15px'
        }}>
        <FormField
          style={{ flex: 1 }}
          fieldId='type'
          type='select'
          label='Type'
          width='100%'
          formData={formData}
          options={typeOptions}
        />
        </div>
        {
          formData.values.type === 'Module' &&
          <div style={{
            width: '100%'
          }}>
            <FormField
              fieldId='moduleBlueprintId'
              type='search'
              label='Session Template'
              formData={formData}
              options={filteredModuleBlueprints}
              onSearch={(val) => setFilterOptionsVal(val)}
              isLoading={isLoading}
              style={{ flex: 1 }}
              width='100%'
              maxHeight='300px'
            />
          </div>
        }
        {
          formData.values.type === 'Transition' &&
          <div style={{
            width: '100%'
          }}>
            <FormField
              fieldId='transitionText'
              type='text'
              label='Transition Text'
              style={{ flex: 1 }}
              formData={formData}
            />
          </div>
        }
      </div>
    </Modal>
  )
}